import { useEffect, useState } from "react";
import { getRuntimeJobs } from "../api/ops.ts";
import { useAuth } from "../context/auth.tsx";
import { Layout } from "../components/layout.tsx";

interface Job {
  id: string;
  agent_name: string;
  status: string;
  priority: string;
  created_at: string;
  retry_count: number;
  max_retries: number;
}

const PRIORITY_COLOR: Record<string, string> = {
  CRITICAL: "text-red-400",
  HIGH: "text-orange-400",
  MEDIUM: "text-yellow-400",
  LOW: "text-gray-400",
};

export function OpsQueuePage() {
  const { token } = useAuth();
  const [queued, setQueued] = useState<Job[]>([]);
  const [running, setRunning] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void Promise.all([
      getRuntimeJobs(token, { status: "QUEUED", limit: 50 }),
      getRuntimeJobs(token, { status: "RUNNING", limit: 50 }),
    ]).then(([q, r]: unknown[]) => {
      setQueued((q as { items: Job[] }).items ?? []);
      setRunning((r as { items: Job[] }).items ?? []);
      setLoading(false);
    });
  }, []);

  return (
    <Layout>
      <div className="mx-auto max-w-6xl p-6">
        <h1 className="mb-6 text-2xl font-bold text-white">Queue Monitor</h1>
        {loading ? (
          <p className="text-gray-400">Loading...</p>
        ) : (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div>
              <h2 className="mb-3 text-lg font-semibold text-white">
                Running <span className="ml-2 text-sm text-blue-400">({running.length})</span>
              </h2>
              <div className="space-y-2">
                {running.length === 0 && <p className="text-sm text-gray-500">No running jobs</p>}
                {running.map((job) => (
                  <div key={job.id} className="rounded border border-blue-900/50 bg-gray-900 p-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-white">{job.agent_name}</span>
                      <span
                        className={`text-xs font-medium ${PRIORITY_COLOR[job.priority] ?? "text-gray-400"}`}
                      >
                        {job.priority}
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      {new Date(job.created_at).toLocaleString()}
                    </p>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h2 className="mb-3 text-lg font-semibold text-white">
                Queued <span className="ml-2 text-sm text-yellow-400">({queued.length})</span>
              </h2>
              <div className="space-y-2">
                {queued.length === 0 && <p className="text-sm text-gray-500">Queue is empty</p>}
                {queued.map((job) => (
                  <div key={job.id} className="rounded border border-yellow-900/50 bg-gray-900 p-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-white">{job.agent_name}</span>
                      <span
                        className={`text-xs font-medium ${PRIORITY_COLOR[job.priority] ?? "text-gray-400"}`}
                      >
                        {job.priority}
                      </span>
                    </div>
                    {job.retry_count > 0 && (
                      <p className="mt-1 text-xs text-yellow-500">
                        Retry {job.retry_count}/{job.max_retries}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
